import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { safeLocalStorage } from '@/lib/safeStorage'

export type AssessMode = 'QUICK' | 'FULL'

/** 위치. 체크리스트와 동일하게 GPS 또는 주소검색으로 받는다 */
export interface CareLocation {
  type: 'gps' | 'search'
  latitude: number
  longitude: number
  addressText: string
}

interface CareCalculatorState {
  /** 빠른 진단 / 정밀 진단 */
  mode: AssessMode
  /** 현재 문항 인덱스 (0-based) */
  step: number
  /** 문항코드 → 선택지 값 */
  answers: Record<string, string>
  /** 위치 (선택). 없으면 결과 화면에서 그때 물어본다 */
  location: CareLocation | null
  completed: boolean
}

interface CareCalculatorActions {
  setMode: (mode: AssessMode) => void
  setAnswer: (code: string, value: string) => void
  setLocation: (location: CareLocation | null) => void
  goToStep: (step: number) => void
  next: (max: number) => void
  prev: () => void
  complete: () => void
  reset: () => void
}

const initialState: CareCalculatorState = {
  mode: 'QUICK',
  step: 0,
  answers: {},
  location: null,
  completed: false,
}

/**
 * 돌봄지원 계산기 진행 상태.
 *
 * 답변은 건강정보(민감정보)라 <b>이 기기에만</b> 보관한다(persist).
 * 계산할 때는 비인증 API 로 답변을 보내지만 <b>서버에 저장하지 않는다</b> —
 * "보내지 않는다"가 아니라 "남기지 않는다"가 정확한 표현이다.
 */
export const useCareCalculatorStore = create<CareCalculatorState & CareCalculatorActions>()(
  persist(
    (set) => ({
      ...initialState,

      setMode: (mode) => set({ mode }),

      setAnswer: (code, value) =>
        set((state) => ({ answers: { ...state.answers, [code]: value } })),

      setLocation: (location) => set({ location }),

      goToStep: (step) => set({ step: Math.max(0, step) }),

      next: (max) => set((state) => ({ step: Math.min(state.step + 1, max) })),

      prev: () => set((state) => ({ step: Math.max(state.step - 1, 0) })),

      complete: () => set({ completed: true }),

      /**
       * 답변·진행만 비운다. **모드는 유지한다.**
       *
       * initialState 를 통째로 덮으면 mode 가 QUICK 으로 돌아가, 정밀 진단을 고르고
       * 시작한 사람이 빠른 진단 문항을 받게 된다(시작 버튼이 reset 을 먼저 부른다).
       * 모드는 답변이 아니라 이번 진단을 어떻게 할지에 대한 선택이므로 남긴다.
       */
      reset: () => set((state) => ({ ...initialState, mode: state.mode })),
    }),
    {
      name: 'cb_care_calculator',
      storage: createJSONStorage(() => safeLocalStorage),
    },
  ),
)
